import { getFormProps, getInputProps, useForm } from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod/v4'
import { useState } from 'react'
import {
	type LoaderFunctionArgs,
	type MetaFunction,
	useNavigate,
	useSearchParams,
} from 'react-router'
import { ClientOnly } from 'remix-utils/client-only'
import { z } from 'zod'
import { LoadingIndicator } from '#app/components/application/loading-indicator/loading-indicator.tsx'
import { Button } from '#app/components/base/buttons/button.tsx'
import { Input } from '#app/components/base/input/input.tsx'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import { useLogin, getAuthErrorMessage } from '#app/hooks/use-auth-mutations.ts'
import { requireAnonymous } from '#app/utils/auth.server.ts'
import { usePreserveNuqsParams } from '#app/utils/navigation.ts'
import { PasswordSchema, EmailSchema } from '#app/utils/user-validation.ts'

const LoginFormSchema = z.object({
	email: EmailSchema,
	password: PasswordSchema,
	redirectTo: z.string().optional(),
	name__confirm: z.string().optional(), // honeypot
})

export async function loader({ request }: LoaderFunctionArgs) {
	await requireAnonymous(request)
	return {}
}

export async function clientLoader() {
	return null
}

export default function LoginPage() {
	const navigate = useNavigate()
	const [searchParams] = useSearchParams()
	const redirectTo = searchParams.get('redirectTo')
	const buildUrl = usePreserveNuqsParams(['redirectTo'])
	const [serverError, setServerError] = useState<string | null>(null)

	const loginMutation = useLogin({
		onSuccess: () => {
			// OAuth flow: Better Auth passes OAuth params directly to /login
			// After login, redirect back to authorize endpoint with all params
			if (searchParams.has('response_type')) {
				const url = new URL(
					'/api/auth/oauth2/authorize',
					window.location.origin,
				)
				searchParams.forEach((v, k) => url.searchParams.set(k, v))
				window.location.href = url.toString()
				return
			}

			// Normal login flow
			void navigate(buildUrl(redirectTo || '/'))
		},
		onError: (error) => {
			setServerError(getAuthErrorMessage(error))
		},
	})

	const [form, fields] = useForm({
		id: 'login-form',
		constraint: getZodConstraint(LoginFormSchema),
		defaultValue: { redirectTo },
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: LoginFormSchema })
		},
		shouldRevalidate: 'onBlur',
		onSubmit(event, { formData }) {
			event.preventDefault()
			setServerError(null)

			const submission = parseWithZod(formData, { schema: LoginFormSchema })
			if (submission.status !== 'success') return
			if (submission.value.name__confirm) return // honeypot

			loginMutation.mutate({
				email: submission.value.email,
				password: submission.value.password,
				remember: true,
			})
		},
	})

	const isPending = loginMutation.isPending

	return (
		<section className="bg-primary relative min-h-screen overflow-hidden px-4 py-12 md:px-8 md:pt-24">
			<div className="relative z-10 mx-auto flex w-full flex-col gap-8 sm:max-w-90">
				<div className="flex flex-col items-center gap-6 text-center">
					<div className="z-10 flex flex-col gap-2 md:gap-3">
						<h1 className="text-display-xs text-primary md:text-display-sm font-semibold">
							Welcome back!
						</h1>
						<p className="text-md text-tertiary self-stretch p-0">
							Please enter your details.
						</p>
					</div>
				</div>

				<ClientOnly
					fallback={
						<div className="flex justify-center py-10">
							<LoadingIndicator size="xl" type="line-simple" />
						</div>
					}
				>
					{() => (
						<form {...getFormProps(form)} className="z-10 flex flex-col gap-6">
							{/* Honeypot field - hidden from users, bots fill it */}
							<input
								type="text"
								name="name__confirm"
								style={{ display: 'none' }}
								tabIndex={-1}
								autoComplete="off"
							/>
							<div className="flex flex-col gap-5">
								<Input
									isRequired
									hideRequiredIndicator
									label="Email"
									placeholder="Enter your email"
									size="md"
									{...getInputProps(fields.email, { type: 'email' })}
									autoFocus
									autoComplete="email"
									isInvalid={!!fields.email.errors?.length}
									hint={fields.email.errors?.join(', ')}
								/>
								<Input
									isRequired
									hideRequiredIndicator
									label="Password"
									size="md"
									placeholder="••••••••"
									{...getInputProps(fields.password, { type: 'password' })}
									autoComplete="current-password"
									isInvalid={!!fields.password.errors?.length}
									hint={fields.password.errors?.join(', ')}
								/>
							</div>

							<div className="flex items-center justify-end">
								<Button
									color="link-color"
									size="md"
									href={buildUrl('/forgot-password')}
								>
									Forgot password
								</Button>
							</div>

							<input
								{...getInputProps(fields.redirectTo, { type: 'hidden' })}
							/>

							{form.errors?.length || serverError ? (
								<div className="bg-error-secondary rounded-lg px-4 py-3">
									<ul className="flex flex-col gap-1">
										{form.errors?.map((error) => (
											<li key={error} className="text-error-primary text-sm">
												{error}
											</li>
										))}
										{serverError && (
											<li className="text-error-primary text-sm">
												{serverError}
											</li>
										)}
									</ul>
								</div>
							) : null}

							<div className="flex flex-col gap-4">
								<Button
									type="submit"
									size="lg"
									isLoading={isPending}
									isDisabled={isPending}
								>
									Sign in
								</Button>
							</div>
						</form>
					)}
				</ClientOnly>
			</div>
		</section>
	)
}

export const meta: MetaFunction = ({ matches }) => {
	const rootData = matches.find((m) => m.id === 'root')?.data as
		| { domainConfig?: { name?: string } }
		| undefined
	const siteName = rootData?.domainConfig?.name ?? 'Iconited'
	return [{ title: `Login to ${siteName}` }]
}

export function ErrorBoundary() {
	return <GeneralErrorBoundary />
}
